import { NotificationsProvider } from "@/app/notifications";
import { PageContent } from "@/components/PageContent";
import { AdaptiveBody } from "@/components/adaptive-body";
import { OptionalAuthProvider } from "@/components/auth-provider";
import { I18nProvider } from "@/components/i18n-provider";
import { PostHogPageView } from "@/components/posthog-pageview";
import { Toaster } from "@/components/shadcn/toaster";
import { ThemeProvider } from "@/components/theme-provider";
import { GlobalStateProvider } from "@/lib/states";
import { AppErrorBoundary } from "@/components/error-boundary";
import ImpersonationBanner from "@/components/impersonation-banner";
import React from "react";
import { AnimatePresence } from "framer-motion";
import { Outlet, useLocation } from "react-router-dom";
import Providers from "./providers";
// Design tokens (:root + .dark CSS variables) must load before globals.css,
// which references them via `hsl(var(--…))`.
import "@stockline/ui/tokens.css";
import "./globals.css";
import { PageTransition } from "@/components/page-transition";
import { usePageTitle } from "@/hooks/use-page-title";

// Lazy + guarded so the virtual:pwa-register/react import is dead-code-eliminated
// out of the capacitor build (which has no vite-plugin-pwa).
const ReloadPrompt =
  __TARGET__ !== "capacitor"
    ? React.lazy(() =>
        import("@/components/ReloadPrompt").then((m) => ({
          default: m.ReloadPrompt,
        })),
      )
    : () => null;

function PageTitleUpdater() {
  usePageTitle();
  return null;
}

export default function RootLayout({
  children,
}: {
  // The web router renders RootLayout as a data-router layout (no children) and
  // its routes fill the <Outlet/>. The mobile router renders it as a plain shell
  // with <AnimatedRoutes> passed as children, so react-router-animated owns the
  // page transitions (and the framer PageTransition below is skipped).
  readonly children?: React.ReactNode;
}) {
  const { pathname } = useLocation();
  // Key PageTransition on the route SECTION (first path segment) instead
  // of the full pathname so navigation between nested children — e.g.
  // /recap → /recap/<id> → /recap — doesn't remount the parent route's
  // layout. Without this, React Router's nested-route layout pattern is
  // defeated: the workspace layout's chat panel (and any other long-lived
  // state held above the Outlet) gets unmounted on every URL flip inside
  // a section. Cross-section nav (/recap → /partners) still animates.
  const transitionKey = pathname.split("/")[1] || "root";

  return (
    <AdaptiveBody>
      <ThemeProvider
        attribute="class"
        defaultTheme="light"
        enableSystem={false}
        disableTransitionOnChange
      >
        <GlobalStateProvider>
          <NotificationsProvider>
            <Providers>
              <PostHogPageView />
              <OptionalAuthProvider>
                <I18nProvider>
                  <PageTitleUpdater />
                  {/* Mounted above PageContent so an active impersonation is
                      flagged on every route — including the public auth-flow
                      pages that render outside the authenticated shell. */}
                  <ImpersonationBanner />
                  <PageContent>
                    <AppErrorBoundary>
                      {children ?? (
                        <AnimatePresence mode="wait">
                          <PageTransition key={transitionKey}>
                            <Outlet />
                          </PageTransition>
                        </AnimatePresence>
                      )}
                    </AppErrorBoundary>
                  </PageContent>
                  <Toaster />
                </I18nProvider>
              </OptionalAuthProvider>
              {__TARGET__ !== "capacitor" && (
                <React.Suspense fallback={null}>
                  <ReloadPrompt />
                </React.Suspense>
              )}
            </Providers>
          </NotificationsProvider>
        </GlobalStateProvider>
      </ThemeProvider>
    </AdaptiveBody>
  );
}
